import Sidebar from '../components/Sidebar';
import ContestTable3 from '../components/ContestTable3';
import ContestTable2 from '../components/ContestTable2';
import ContestTable from '../components/ContestTable';
import Topbar from '../components/Topbar';
import ContestFilter from '../components/ContestFilter';
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../utils/Instance';

export default function Contests() {
  const [contests, setContests] = useState([]);
  const [joinedContests, setJoinedContests] = useState([]);
  const [allContests, setAllContests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { profile } = useAuth();
  const role = profile?.role || "";
  
  // Helper function for date formatting
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Helper function for contest status
  const getContestStatus = (startTime, endTime) => {
    const now = new Date();
    const start = new Date(startTime);
    const end = new Date(endTime);
    if (now >= end) return "Ended";
    if (now >= start && now < end) return "In Progress";
    return "Open";
  };

  // Filter contests that have been completed for more than 24 hours
  const filterExpiredContests = (contestsList = []) => {
    const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
    const now = Date.now();

    return contestsList.filter((contest) => {
      const rawEndTime = contest?.end_time || contest?.endTime || contest?.endDate;

      if (!rawEndTime) {
        return true;
      }

      const endTime = new Date(rawEndTime).getTime();

      if (Number.isNaN(endTime)) {
        return true;
      }

      const elapsedMs = now - endTime;

      if (elapsedMs <= 0) {
        return true;
      }

      return elapsedMs <= TWENTY_FOUR_HOURS_MS;
    });
  };

  // Callback to refresh data after joining a contest
  const handleJoinSuccess = async () => {
    try {
      // Refresh joined contests
      const joinedResponse = await fetch(`${API_BASE}accounts/contests/my-contests/`, {
        headers: {
          'Authorization': `Bearer ${profile.accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (joinedResponse.ok) {
        const joinedData = await joinedResponse.json();
        setJoinedContests(filterExpiredContests(joinedData));
      }
    } catch (error) {
      console.error('Error refreshing joined contests:', error);
    }
  };

  useEffect(() => {
    const fetchContests = async () => {
      try {
        setLoading(true);
        
        // Check if user is authenticated
        if (!profile?.accessToken) {
          throw new Error('No authentication token found');
        }

        // For users, fetch both all contests and joined contests
        if (role === "user") {
          // Fetch joined contests
          const joinedResponse = await fetch(`${API_BASE}accounts/contests/my-contests/`, {
            headers: {
              'Authorization': `Bearer ${profile.accessToken}`,
              'Content-Type': 'application/json'
            }
          });

          if (joinedResponse.ok) {
            const joinedData = await joinedResponse.json();
            setJoinedContests(filterExpiredContests(joinedData));
          }
        }
        
        // Fetch all contests
        const response = await fetch(`${API_BASE}accounts/contests/?t=${Date.now()}`, {
          headers: {
            'Authorization': `Bearer ${profile.accessToken}`,
            'Content-Type': 'application/json'
          },
          cache: 'no-store'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        const filteredData = filterExpiredContests(data);
        setContests(filteredData);
        setAllContests(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching contests:', err);
        setError(err.message || 'Failed to fetch contests');
      } finally {
        setLoading(false);
      }
    };

    fetchContests();
  }, [profile?.accessToken, role]);

  return (
    <div className="flex min-h-screen bg-black">
      <Sidebar />
      <div className="flex-1 p-4 md:p-6 lg:ml-64 w-full">
        <Topbar pageTitle="Contests"/>

        <div className="overflow-hidden">
          <ContestFilter role={role} />
        </div>

        {loading ? (
          <div className="py-6 md:py-10 mt-3 md:mt-5 flex justify-center items-center">
            <div className="text-white text-lg">Loading contests...</div>
          </div>
        ) : error ? (
          <div className="py-6 md:py-10 mt-3 md:mt-5 flex justify-center items-center">
            <div className="text-red-500 text-lg">Error: {error}</div>
          </div>
        ) : (
          <div className="py-6 md:py-10 mt-3 md:mt-5 overflow-x-auto no-scrollbar overflow-y-auto">
            {role === "user" ? (
              (() => {
                const filteredJoinedContests = filterExpiredContests(joinedContests);
                const filteredContests = filterExpiredContests(contests);
                
                // Filter available contests (not joined, not ended)
                const availableContests = filteredContests.filter(contest => {
                  const joinedIds = filteredJoinedContests.map(c => c.id);
                  const isJoined = joinedIds.includes(contest.id);
                  const status = getContestStatus(contest.start_time, contest.end_time);
                  return !isJoined && status !== "Ended";
                });
                
                return (
              <>
                {/* Show Available Contests to Join */}
                {availableContests.length > 0 && (
                  <div className="mb-10">
                    <h2 className="text-xl font-semibold mb-4 font-inter text-[#ceb46a]">Available Contests</h2>
                    <ContestTable
                      data={availableContests.map(c => ({
                        id: c.id,
                        name: c.title,
                        status: getContestStatus(c.start_time, c.end_time),
                        startDate: formatDate(c.start_time),
                        endDate: formatDate(c.end_time),
                        maxEntrants: c.max_participants,
                        category: c.category,
                        cost: c.cost,
                        recurring: c.recurring,
                        joined: false,
                      }))}
                      setContests={setContests}
                      showInProgress={false}
                      onJoinSuccess={handleJoinSuccess}
                    />
                  </div>
                )}

                {/* Show Joined Contests with Open button */}
                {filteredJoinedContests.length > 0 && (
                  <div className="mb-10">
                    <h2 className="text-xl font-semibold mb-4 font-inter text-[#ceb46a]">Your Active Contests</h2>
                    <ContestTable
                      data={filteredJoinedContests.map(c => ({
                        id: c.id,
                        name: c.title,
                        status: getContestStatus(c.start_time, c.end_time),
                        startDate: formatDate(c.start_time),
                        endDate: formatDate(c.end_time),
                        maxEntrants: c.max_participants,
                        category: c.category,
                        cost: c.cost,
                        recurring: c.recurring,
                        joined: true,
                      }))}
                      setContests={setJoinedContests}
                      showInProgress={true}
                      onJoinSuccess={handleJoinSuccess}
                    />
                  </div>
                )}

                {/* Show Contest Results */}
                <h2 className="text-xl font-semibold mb-4 font-inter text-[#ceb46a]">Contest Results</h2>
                <ContestTable2
                data={allContests.map((c) => ({
                  id: c.id,
                  title: c.title || c.name || "N/A",
                  name: c.title || c.name || "N/A",
                  gallery: c.gallery || "N/A",
                  end_time: c.end_time,
                }))}
                />
              </>
                );
              })()
            ) : (
              <ContestTable3 role={role} data={filterExpiredContests(contests)} setContests={setContests} />
            )}
          </div>
        )}
        
      </div>
    </div>
  );
}